import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { Icon, Label, Stack, TextField, Checkbox } from '@bingads-webui-react/fabric-control';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';

const defaultLabelStyle = options => ({
  label: {
    color: options.success ? '#0A610A' : '#A80000',
    backgroundColor: options.success ? '#DFF6DD' : '#FDE7E9',
    fontWeight: 600,
    fontSize: 12,
    border: 4,
    paddingInline: 5,
    paddingTop: 2,
    paddingBottom: 2,
    verticalAlign: 'middle',
    borderRadius: 4,
    marginLeft: 8,
  },
  labelIcon: {
    fontSize: 14,
    height: 14,
    width: 14,
    fontWeight: 400,
    marginLeft: 4,
    marginRight: 5,
    verticalAlign: '-2.5px',
  },
});

const defaultStyles = {
  disconnectedStatusTile: {
    root: {
    },
  },
  connectedStatusTile: {
    root: {
      marginLeft: 22,
    },
  },
  connectedStatusLabel: {
    root: {
      color: '#0C5E0C',
      fontWeight: 600,
      fontSize: 12,
      lineHeight: 'normal',
    },
  },
  saveButton: {
    root: {
      width: 68,
    },
  },
  removeButton: {
    root: {
      width: 89,
    },
  },
};

const VerifiedTrackingSettingKey = 'VerifiedTrackingSettings';
const vendorNameKey = 'vendorName';
const advertiserEntityIdKey = 'advertiserEntityId';
const publisherEntityIdKey = 'publisherEntityId';

const StatusLabel = ({
  value, i18n, success, type,
}) => {
  let statusMessage = i18n.getString(_TL_('Unknown status'));
  if (type === advertiserEntityIdKey) {
    statusMessage = success ? i18n.getString(_TL_('IAS Advertiser ID: {0}')).replace('{0}', value) : i18n.getString(_TL_('Invalid Advertiser ID'));
  } else if (type === publisherEntityIdKey) {
    statusMessage = success ? i18n.getString(_TL_('IAS Publisher ID: {0}')).replace('{0}', value) : i18n.getString(_TL_('Invalid Publisher ID'));
  }

  const statusStyle = useMemo(() => defaultLabelStyle({ success }), [success]);

  return (
    <Label
      style={statusStyle.label}
      className={`status-label-${type}`}
    >
      <Icon
        iconName={success ? 'Accept' : 'Blocked2'}
        style={statusStyle.labelIcon}
      />
      {statusMessage}
    </Label>
  );
};

const createVerifiedTrackingSettingViewModel = value => ({
  [VerifiedTrackingSettingKey]: value,
});

const getCheckStatus = (settings, vendor) => {
  if (_.isEmpty(settings) || !settings[vendor]) {
    return false;
  }

  return settings[vendor].enabled;
};

export const ThirdPartyMeasurementV2 = (props) => {
  const {
    i18n,
    updateValidation,
    validationProps,
    overrideStyles = {},
  } = props;

  const { data } = validationProps;
  const [settings, setSettings] = useState({});
  const [errors, setErrors] = useState({});

  const verifiedTrackingDatas = useMemo(
    () => _.result(data, [VerifiedTrackingSettingKey], [])
    , [data]
  );
  const styles = useMemo(() => ({
    ...defaultStyles,
    ...overrideStyles,
  }), [overrideStyles]);

  useEffect(() => {
    const settingsToUpdate = {};
    if (verifiedTrackingDatas.length > 0) {
      const iasData = verifiedTrackingDatas.find(setting =>
        setting.vendorName === 'IAS');
      const moatData = verifiedTrackingDatas.find(setting =>
        setting.vendorName === 'MOAT');

      if (!_.isEmpty(iasData)) {
        settingsToUpdate.IAS = {
          enabled: true,
          ...iasData,
        };
      } else {
        settingsToUpdate.IAS = {
          enabled: false,
        };
      }

      if (!_.isEmpty(moatData)) {
        settingsToUpdate.MOAT = {
          enabled: true,
          ...moatData,
        };
      } else {
        settingsToUpdate.MOAT = {
          enabled: false,
        };
      }

      setSettings(settingsToUpdate);
    } else {
      settingsToUpdate.IAS = { enabled: false };
      settingsToUpdate.MOAT = { enabled: false };
      setSettings(settingsToUpdate);
    }
  }, [verifiedTrackingDatas]);

  useEffect(() => {
    if (_.isEmpty(settings)) {
      return;
    }
    // Validate IAS
    let iasSettingsToUpdate = { [vendorNameKey]: 'IAS' };
    const iasEnabled = _.get(settings, ['IAS', 'enabled'], false);
    if (iasEnabled) {
      const advertiserEntityId = _.get(settings, ['IAS', advertiserEntityIdKey], '');
      const publisherEntityId = _.get(settings, ['IAS', publisherEntityIdKey], '');
      const numericPattern = /^\d+$/;
      const errorsToUpdate = {};
      if (!numericPattern.test(advertiserEntityId)) {
        errorsToUpdate[advertiserEntityIdKey] = advertiserEntityIdKey;
      }

      if (!numericPattern.test(publisherEntityId)) {
        errorsToUpdate[publisherEntityIdKey] = publisherEntityIdKey;
      }
      if (!_.isEmpty(errorsToUpdate)) {
        setErrors(errorsToUpdate);
      } else {
        setErrors({});
      }
      iasSettingsToUpdate = {
        ...iasSettingsToUpdate,
        enabled: true,
        advertiserEntityId,
        publisherEntityId,
      };
    } else {
      iasSettingsToUpdate.enabled = false;
    }

    // validate MOAT
    const moatSettingsToUpdate = {
      enabled: _.get(settings, ['MOAT', 'enabled'], false),
      [vendorNameKey]: 'MOAT',
    };

    // update validation
    updateValidation([], createVerifiedTrackingSettingViewModel([iasSettingsToUpdate, moatSettingsToUpdate]));
  }, [settings, updateValidation]);

  const onRenderadvertiserIdTextField = useCallback(() => (
    <Stack verticalAlign="center" horizontal>
      <Label>
        {i18n.getString(_TL_('advertiser id'))}
      </Label>
      <HelpPopupInline topic="pop_BA_VideoAds_TagURL" />
    </Stack>
  ), [i18n]);

  const onRenderpublisherIdTextField = useCallback(() => (
    <Stack verticalAlign="center" horizontal>
      <Label>
        {i18n.getString(_TL_('publisher id'))}
      </Label>
      <HelpPopupInline topic="pop_BA_VideoAds_TagURL" />
    </Stack>
  ), [i18n]);

  const onAdvertiserIdChange = useCallback((event, value) => {
    if (!_.isEmpty(settings.IAS) && !!settings.IAS.enabled) {
      setSettings({
        ...settings,
        IAS: {
          ...settings.IAS,
          advertiserEntityId: value,
        },
      });
    }
  }, [settings]);

  const onPublisherIdChange = useCallback((event, value) => {
    if (!_.isEmpty(settings.IAS) && !!settings.IAS.enabled) {
      setSettings({
        ...settings,
        IAS: {
          ...settings.IAS,
          publisherEntityId: value,
        },
      });
    }
  }, [settings]);

  const onCheckStatusChangeGenerator = useCallback(vendor => (event, checked) => {
    setSettings((originalSettings) => {
      const newSettings = { ...originalSettings };
      if (_.isEmpty(newSettings[vendor])) {
        newSettings[vendor] = {
          enabled: checked,
        };
      } else {
        newSettings[vendor].enabled = checked;
      }
      return newSettings;
    });
  }, []);

  const tagErrorMessage = useCallback((errorType) => {
    if (!_.isEmpty(errors) && !!errors[errorType]) {
      return (
        <Stack className="tag-error-message" horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
          <StatusLabel type={errors[errorType]} i18n={i18n} success={false} />
        </Stack>
      );
    }
    return '';
  }, [errors, i18n]);

  const onRenderIASLabel = useCallback(() => (
    <Label className="ias-name-label">
      {i18n.getString(_TL_('Integral Ad Science'))}
    </Label>
  ), [i18n]);

  const onRenderMOATLabel = useCallback(() => (
    <Label className="moat-name-label">
      {i18n.getString(_TL_('Oracle MOAT'))}
    </Label>
  ), [i18n]);

  const onRenderIASCheckbox = useCallback(() => (
    <Stack className="ias-choice" horizontal tokens={{ childrenGap: 8 }} style={styles.disconnectedStatusTile.root}>
      <Checkbox className="ias-checkbox" onChange={onCheckStatusChangeGenerator('IAS')} onRenderLabel={onRenderIASLabel} checked={getCheckStatus(settings, 'IAS')} />
      <TextField
        className="ias-advertiser-id-input"
        autoAdjustHeight
        onRenderLabel={onRenderadvertiserIdTextField}
        onChange={onAdvertiserIdChange}
        errorMessage={tagErrorMessage(advertiserEntityIdKey)}
        disabled={!_.get(settings, ['IAS', 'enabled'], false)}
        value={_.get(settings, ['IAS', advertiserEntityIdKey], '')}
      />
      <TextField
        className="ias-publisher-id-input"
        autoAdjustHeight
        onRenderLabel={onRenderpublisherIdTextField}
        onChange={onPublisherIdChange}
        errorMessage={tagErrorMessage(publisherEntityIdKey)}
        disabled={!_.get(settings, ['IAS', 'enabled'], false)}
        value={_.get(settings, ['IAS', publisherEntityIdKey], '')}
      />
    </Stack>
  ), [styles.disconnectedStatusTile.root, onCheckStatusChangeGenerator, onRenderIASLabel, settings, onRenderadvertiserIdTextField, onAdvertiserIdChange, tagErrorMessage, onRenderpublisherIdTextField, onPublisherIdChange]);

  return (
    <div className="third-party-measurement-tile-V2">
      <Stack className="third-party-measurement-choices" horizontal={false} tokens={{ childrenGap: 8 }} style={styles.disconnectedStatusTile.root}>
        {onRenderIASCheckbox()}
        <Checkbox className="moat-checkbox" onChange={onCheckStatusChangeGenerator('MOAT')} onRenderLabel={onRenderMOATLabel} checked={getCheckStatus(settings, 'MOAT')} />
      </Stack>
    </div>
  );
};

ThirdPartyMeasurementV2.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      VerifiedTrackingSettings: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  overrideStyles: PropTypes.shape({}),
};

ThirdPartyMeasurementV2.defaultProps = {
  overrideStyles: {},
};

StatusLabel.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  success: PropTypes.bool.isRequired,
  value: PropTypes.string,
};

StatusLabel.defaultProps = {
  value: '',
};

export const ThirdPartyMeasurementV2WithValidation = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(ThirdPartyMeasurementV2))));
